import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Stack, Switch, TextField, FormControlLabel,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import api from '../../services/api';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import DataTableCard from '../../components/ui/DataTableCard';
import EnhancedDataGrid from '../../components/ui/EnhancedDataGrid';
import type { GridColDef } from '@mui/x-data-grid';
import { formatMoney } from '../../utils/currency';

interface MerchantPromoRow {
  _id: string;
  code: string;
  title?: string;
  description?: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  maxDiscountAmount?: number;
  minOrderAmount?: number;
  usageLimit?: number;
  usageLimitPerUser?: number;
  isActive: boolean;
  isFirstOrderOnly?: boolean;
  validFrom?: string;
  validUntil?: string;
}

const emptyForm = () => ({
  code: '',
  title: '',
  description: '',
  discountType: 'percentage' as const,
  discountValue: 10,
  maxDiscountAmount: '',
  minOrderAmount: 0,
  usageLimit: '',
  usageLimitPerUser: 1,
  isActive: true,
  isFirstOrderOnly: false,
  validUntil: '',
});

export default function MerchantPromosPage() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<MerchantPromoRow | null>(null);
  const [form, setForm] = useState(emptyForm);

  const { data: promos = [], isLoading } = useQuery({
    queryKey: ['merchant-promos'],
    queryFn: async () => {
      const res = await api.get('/merchant/promos');
      return (res.data.data || []) as MerchantPromoRow[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        code: form.code.trim().toUpperCase(),
        title: form.title || form.code,
        description: form.description || undefined,
        discountType: form.discountType,
        discountValue: Number(form.discountValue) || 0,
        maxDiscountAmount: form.maxDiscountAmount ? Number(form.maxDiscountAmount) : undefined,
        minOrderAmount: Number(form.minOrderAmount) || 0,
        usageLimit: form.usageLimit ? Number(form.usageLimit) : undefined,
        usageLimitPerUser: Number(form.usageLimitPerUser) || 1,
        isActive: form.isActive,
        isFirstOrderOnly: form.isFirstOrderOnly,
        validUntil: form.validUntil || undefined,
      };
      if (editing?._id) {
        return api.put(`/merchant/promos/${editing._id}`, payload);
      }
      return api.post('/merchant/promos', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchant-promos'] });
      setOpen(false);
      setEditing(null);
      setForm(emptyForm());
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/merchant/promos/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['merchant-promos'] }),
  });

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (row: MerchantPromoRow) => {
    setEditing(row);
    setForm({
      code: row.code,
      title: row.title || '',
      description: row.description || '',
      discountType: row.discountType || 'percentage',
      discountValue: row.discountValue ?? 10,
      maxDiscountAmount: row.maxDiscountAmount != null ? String(row.maxDiscountAmount) : '',
      minOrderAmount: row.minOrderAmount ?? 0,
      usageLimit: row.usageLimit != null ? String(row.usageLimit) : '',
      usageLimitPerUser: row.usageLimitPerUser ?? 1,
      isActive: row.isActive !== false,
      isFirstOrderOnly: row.isFirstOrderOnly === true,
      validUntil: row.validUntil ? row.validUntil.slice(0, 10) : '',
    });
    setOpen(true);
  };

  const columns: GridColDef[] = [
    { field: 'code', headerName: 'Code', width: 120 },
    { field: 'title', headerName: 'Title', flex: 1, minWidth: 140 },
    {
      field: 'discountValue', headerName: 'Discount', width: 110,
      valueGetter: (_v, row) => row.discountType === 'fixed' ? `${formatMoney(row.discountValue)}` : `${row.discountValue}%`,
    },
    { field: 'minOrderAmount', headerName: 'Min order', width: 100, valueGetter: (_v, row) => `${formatMoney(row.minOrderAmount ?? 0)}` },
    {
      field: 'isActive', headerName: 'Status', width: 100,
      renderCell: (p) => <Chip size="small" label={p.value ? 'Active' : 'Off'} color={p.value ? 'success' : 'default'} />,
    },
    {
      field: 'actions', headerName: '', width: 160, sortable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={() => openEdit(p.row as MerchantPromoRow)}>Edit</Button>
          <Button size="small" color="error" onClick={() => deleteMutation.mutate(p.row._id)}>Delete</Button>
        </Stack>
      ),
    },
  ];

  return (
    <PageShell>
      <PageHeader
        title="Offers & Coupons"
        subtitle="Restaurant promo codes for your food store"
        actions={
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
            New promo
          </Button>
        }
      />
      <DataTableCard title="Your promos" subtitle="Discount codes applied at food checkout for your restaurant">
        <EnhancedDataGrid
          rows={promos}
          columns={columns}
          loading={isLoading}
          getRowId={(r) => r._id}
          autoHeight
          disableRowSelectionOnClick
        />
      </DataTableCard>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit promo' : 'Create promo'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Promo code" value={form.code} disabled={!!editing}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} required />
            <TextField label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <TextField label="Description" value={form.description} multiline minRows={2}
              onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <TextField select label="Discount type" value={form.discountType}
              onChange={(e) => setForm({ ...form, discountType: e.target.value as 'percentage' | 'fixed' })}>
              <MenuItem value="percentage">Percentage (%)</MenuItem>
              <MenuItem value="fixed">Fixed amount (₹)</MenuItem>
            </TextField>
            <TextField label="Discount value" type="number" value={form.discountValue}
              onChange={(e) => setForm({ ...form, discountValue: Number(e.target.value) })} />
            <TextField label="Max discount (₹)" type="number" value={form.maxDiscountAmount}
              onChange={(e) => setForm({ ...form, maxDiscountAmount: e.target.value })} />
            <TextField label="Min order (₹)" type="number" value={form.minOrderAmount}
              onChange={(e) => setForm({ ...form, minOrderAmount: Number(e.target.value) })} />
            <TextField label="Valid until" type="date" value={form.validUntil} InputLabelProps={{ shrink: true }}
              onChange={(e) => setForm({ ...form, validUntil: e.target.value })} />
            <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />}
              label="Active" />
            <FormControlLabel control={<Switch checked={form.isFirstOrderOnly} onChange={(e) => setForm({ ...form, isFirstOrderOnly: e.target.checked })} />}
              label="First order only" />
            {saveMutation.isError && (
              <Alert severity="error">{(saveMutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Save failed'}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !form.code.trim()}>
            {editing ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}